import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Building2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

export default function CompanyChooser() {
  const navigate = useNavigate();
  const { token, pendingCompanySelection, loadCompanies, selectCompany, logout } = useAuth();
  const [companies, setCompanies] = useState(pendingCompanySelection?.companies || []);
  const [loading, setLoading] = useState(!pendingCompanySelection);
  const [working, setWorking] = useState("");

  useEffect(() => {
    if (pendingCompanySelection) { setCompanies(pendingCompanySelection.companies); setLoading(false); return; }
    if (!token) { navigate("/login", { replace: true }); return; }
    loadCompanies().then(setCompanies).catch(() => toast.error("Impossibile caricare le società"))
      .finally(() => setLoading(false));
  }, [token, pendingCompanySelection, navigate, loadCompanies]);

  const select = async (societaId) => {
    setWorking(societaId);
    try {
      await selectCompany(societaId);
      navigate("/", { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Accesso non autorizzato");
    } finally { setWorking(""); }
  };

  return (
    <main className="min-h-screen px-5 py-8 sm:py-16 bg-[#F5F5FA] text-slate-900" data-testid="company-chooser">
      <div className="mx-auto max-w-2xl">
        <button type="button" data-testid="company-chooser-back" onClick={() => {
          if (pendingCompanySelection) { logout(); navigate("/login"); }
          else navigate("/");
        }} className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 transition-colors">
          <ArrowLeft className="h-4 w-4" /> {pendingCompanySelection ? "Torna all’accesso" : "Torna alla dashboard"}
        </button>
        <img src="/yollo-logo.png" alt="Yollo" className="w-20 h-auto mt-12 mb-8" />
        <span className="text-xs uppercase font-bold text-teal-700">Le tue società</span>
        <h1 className="text-4xl sm:text-5xl font-black mt-2 mb-4">Con quale società vuoi entrare?</h1>
        <p className="text-sm sm:text-base text-slate-500 mb-10">Seleziona la Partita IVA da gestire.</p>
        {loading ? <div data-testid="company-chooser-loading" className="flex items-center gap-3 text-slate-500"><Loader2 className="animate-spin h-5 w-5" /> Caricamento…</div> : (
          <div className="grid gap-3">
            {companies.map(company => (
              <button type="button" key={company.societa_id} data-testid={`company-select-${company.societa_id}`}
                disabled={Boolean(working)} onClick={() => select(company.societa_id)}
                className="flex w-full min-w-0 items-center gap-4 bg-white border border-slate-200 p-5 text-left hover:border-teal-500 hover:shadow-sm focus-visible:outline-teal-500 disabled:opacity-60 transition-[border-color,box-shadow,opacity]">
                <span className="w-11 h-11 flex-shrink-0 grid place-items-center bg-teal-50 text-teal-700"><Building2 className="h-5 w-5" /></span>
                <span className="flex-1 min-w-0"><strong className="block text-base break-words" data-testid={`company-name-${company.societa_id}`}>{company.nome}</strong>
                  <small className="text-slate-500" data-testid={`company-vat-${company.societa_id}`}>P.IVA {company.partita_iva || "—"}</small></span>
                {working === company.societa_id ? <Loader2 className="h-5 w-5 animate-spin flex-shrink-0" /> : <ArrowRight className="h-5 w-5 flex-shrink-0 text-teal-700" />}
              </button>
            ))}
            {!companies.length && <p data-testid="company-chooser-empty" className="text-sm text-slate-600">Nessuna società attiva trovata per questo account.</p>}
          </div>
        )}
      </div>
    </main>
  );
}